/**
 * Google Analytics 4 Event Tracking Utility
 *
 * All events respect user consent from CookieContext.
 * Events are only sent when analytics consent is granted.
 */

/**
 * Check if analytics is allowed based on consent
 */
const hasAnalyticsConsent = () => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      const parsed = JSON.parse(consent);
      return parsed.analytics === true;
    }
  } catch {
    // Consent not set or error parsing
  }
  return false;
};

/**
 * Send event to Google Analytics
 */
const sendEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag && hasAnalyticsConsent()) {
    window.gtag('event', eventName, params);
  }
};

/**
 * Track Tax Code calculation
 */
export const trackCalculation = (data = {}) => {
  sendEvent('calculate_cf', {
    event_category: 'calculator',
    event_label: data.isForeign ? 'foreign_birth' : 'italian_birth',
    gender: data.gender || 'unknown'
  });
};

/**
 * Track Tax Code verification
 */
export const trackVerification = (isValid = false) => {
  sendEvent('verify_cf', {
    event_category: 'verification',
    event_label: isValid ? 'valid' : 'invalid',
    value: isValid ? 1 : 0
  });
};

/**
 * Track reverse lookup (decode)
 */
export const trackReverseLookup = () => {
  sendEvent('reverse_cf', {
    event_category: 'decoder',
    event_label: 'decode_attempt'
  });
};

/**
 * Track Health Card PDF download
 */
export const trackHealthCardDownload = () => {
  sendEvent('download_health_card', {
    event_category: 'download',
    event_label: 'health_card_pdf'
  });
};

/**
 * Track Tax Code copy to clipboard
 */
export const trackCopyToClipboard = (type = 'cf') => {
  sendEvent('copy_to_clipboard', {
    event_category: 'interaction',
    event_label: type // 'cf', 'health_card', etc.
  });
};

/**
 * Track municipality search
 */
export const trackMunicipalitySearch = (query = '') => {
  sendEvent('search_municipality', {
    event_category: 'search',
    search_term: query.substring(0, 50) // Truncate for privacy
  });
};

/**
 * Track blog article view
 */
export const trackArticleView = (articleSlug = '', locale = 'it') => {
  sendEvent('view_article', {
    event_category: 'blog',
    event_label: articleSlug,
    language: locale
  });
};

/**
 * Track tool page view
 */
export const trackToolView = (toolName = '') => {
  sendEvent('view_tool', {
    event_category: 'tools',
    event_label: toolName
  });
};

/**
 * Track language change
 */
export const trackLanguageChange = (fromLang = '', toLang = '') => {
  sendEvent('change_language', {
    event_category: 'localization',
    event_label: `${fromLang}_to_${toLang}`
  });
};

/**
 * Track share action
 */
export const trackShare = (method = 'unknown', contentType = 'cf') => {
  sendEvent('share', {
    method: method, // 'copy', 'whatsapp', 'email', etc.
    content_type: contentType
  });
};

/**
 * Track form errors (for UX improvement)
 */
export const trackFormError = (formName = '', errorType = '') => {
  sendEvent('form_error', {
    event_category: 'errors',
    event_label: `${formName}_${errorType}`
  });
};

/**
 * Track outbound link clicks
 */
export const trackOutboundLink = (url = '') => {
  sendEvent('click', {
    event_category: 'outbound',
    event_label: url,
    transport_type: 'beacon'
  });
};

/**
 * Track scroll depth (call at 25%, 50%, 75%, 100%)
 */
export const trackScrollDepth = (percentage = 0, pagePath = '') => {
  sendEvent('scroll', {
    event_category: 'engagement',
    event_label: pagePath,
    value: percentage
  });
};

/**
 * Track time on page (call on unmount or visibility change)
 */
export const trackTimeOnPage = (seconds = 0, pagePath = '') => {
  sendEvent('timing_complete', {
    event_category: 'engagement',
    event_label: pagePath,
    value: Math.round(seconds)
  });
};

/**
 * Track an SPA page view (call on route change). Only fires once GA4 is actually
 * loaded — i.e. analytics consent was granted and we are not in the prerender/crawl
 * (see CookieContext.loadAnalytics). GA4's initial gtag('config') already counts the
 * landing page; this covers subsequent client-side route changes.
 */
export const trackPageView = (path) => {
  if (typeof window !== 'undefined' && window.gaLoaded && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path || window.location.pathname,
      page_location: window.location.href,
      page_title: document.title
    });
  }
};

/**
 * Track cookie consent choice
 */
export const trackConsentChoice = (choice = 'unknown') => {
  // This one doesn't check consent since it's about consent itself
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'consent_choice', {
      event_category: 'privacy',
      event_label: choice // 'accept_all', 'reject_non_essential', 'custom'
    });
  }
};

export default {
  trackCalculation,
  trackVerification,
  trackReverseLookup,
  trackHealthCardDownload,
  trackCopyToClipboard,
  trackMunicipalitySearch,
  trackArticleView,
  trackToolView,
  trackLanguageChange,
  trackShare,
  trackFormError,
  trackOutboundLink,
  trackScrollDepth,
  trackTimeOnPage,
  trackPageView,
  trackConsentChoice
};
